import React, { useEffect, useState } from 'react';
import styled from 'styled-components';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Defect, DefectType } from '../../../models/defects';
import { getAircraftMel } from '../../../services/api';
import DeferralReferencePopup from './DeferralReferencePopup';

interface DeferralReferenceWrapperProps {
  defectData: Defect;
  aircraftId: string;
}

const LoadingContainer = styled.div`
  position: relative;
  width: 200px;
  display: flex;
  justify-content: center;
  align-items: center;
`;

const DEFERRABLE_TYPES = [DefectType.MEL, DefectType.NEF, DefectType.CDL, DefectType.CAS];

const DeferralReferenceWrapper: React.FC<DeferralReferenceWrapperProps> = ({ defectData, aircraftId }) => {
  const [shouldShow, setShouldShow] = useState(false);
  const queryClient = useQueryClient();

  useEffect(() => {
    const isDeferred = defectData?.deferred === true;
    const hasDeferrableType = DEFERRABLE_TYPES.includes(defectData?.defect_type);
    const willShow = isDeferred || hasDeferrableType;

    setShouldShow(willShow);

    if (willShow && !defectData?.book_item) {
      queryClient.prefetchQuery(['aircraftMel', aircraftId], () => getAircraftMel(aircraftId));
    }
  }, [defectData?.deferred, defectData?.defect_type, aircraftId, queryClient, defectData?.book_item]);

  const { data: aircraftMel } = useQuery(['aircraftMel', aircraftId], () => getAircraftMel(aircraftId), {
    enabled: shouldShow && !defectData?.book_item,
    refetchOnWindowFocus: false,
  });

  if (!shouldShow) {
    return null;
  }

  const melId = defectData?.book_item?.book_id || aircraftMel?.id;

  if (melId) {
    return (
      <LoadingContainer data-testid="DeferralReferenceWrapper--LoadingContainer">
        <DeferralReferencePopup key={melId} melId={melId} />
      </LoadingContainer>
    );
  }

  return null;
};

export default DeferralReferenceWrapper;
